import { Type } from "class-transformer";
import { MenuItem } from "primeng/api";
import { Dates } from "src/modules/utils/shared/dates";
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from "src/modules/utils/shared/utils";
import { ColumnWidth } from "../models/column-width.model";
import { TableCellEvent } from "../models/table-cell-event.model";
import { TableCell } from "../models/table-cell.model";
import { TableData } from "../models/table-data.model";
import { TableSizer } from "../models/table-sizer.model";
import { FrontendFieldType } from "../netgen/FrontendFieldType";
import { FrontendField } from "./FrontendField";
import { TableCellType } from "./TableCellType";

export class TableColumn {

    //netgen start
    id: string;
    caption: string;
    cellType: TableCellType = TableCellType.string;
    autoWidth = 0;
    editable = false;
    editableMaxLength: number = null;
    //netgen end

    icon: string;
    groupCaption: string;
    resizable = true;
    sortable: boolean = null;
    filterable: boolean = null;
    minWidth: number = null;
    maxWidth: number = null;
    flex: number = null;
    isLink = false;
    frozen: "left" | "right" | null = null;
    align: "left" | "center" | "right" = "left";
    useComparator: boolean = null;
    rotate90 = false;
    keepHtml = false;
    keepHtmlHeader = false;
    toolTip: string = null;
    numDecimalDigits: number = null;
    expandDecimalDigits = false;
    disableFlashOnChanges = false;
    suppressRepeatingValues = false;
    
    styleClass: string = null;
    headerStyleClass: string = null;
    headerAlign: "center" | "left" | "right" | undefined = null;
    useHeaderWidthAsMin = true;


    @Type(() => FrontendField)
    editor: FrontendField | ((TableCellEvent) => FrontendField);
    editorPopup = false;
    
    cellRenderer: (params: any) => string;
    valueFormatter: (value: TableCellEvent) => string;
    valueGetter: (value: TableCellEvent) => any;
    valueSetter: (value: TableCellEvent) => void;
    toolTipGetter: (value: TableCellEvent) => string;
    editableCallback: (value: TableCellEvent) => boolean;
    getStyleClassCallback: (value: TableCellEvent) => string;
    getContextMenuItems: (value: TableCellEvent) => MenuItem[];
    headerClicked: (columnId: string) => boolean | Promise<boolean>;

    calculatedWidth: string = null;

    constructor(id: string, caption: string, cellType, autoWidthOrFlex, props: Partial<TableColumn> = null) {
        this.id = id;
        this.caption = caption;
        this.cellType = cellType;
        this.autoWidth = autoWidthOrFlex >= 0 ? autoWidthOrFlex : 0;
        this.flex = autoWidthOrFlex >= 0 ? null : -autoWidthOrFlex;
        if (props != null) {
            Object.assign(this, props);
        }
        if (this.id != null && this.autoWidth == null && this.flex == null) {
            console.trace("Warnung. Tabellenspalte " + id + "/" + caption + ": Keine Breite und kein Flex angegeben");
        }
    }

    getEditor(getEventCallback: () => TableCellEvent): FrontendField {
        return Utils.isFunction(this.editor) ? (this.editor as any)(getEventCallback()) : this.editor as FrontendField;                        
    }

    getRealWidth(table: TableData, byContent: boolean, theme: string): ColumnWidth {
        return TableSizer.get(theme).calculate(table, this, byContent, this.useHeaderWidthAsMin);
    }

    isEditable(event: TableCellEvent): boolean {
        return this.editable && (this.editableCallback == null || this.editableCallback(event));
    }

    getValue(value: any, tableCellEventGenerator: () => TableCellEvent): string {
        return this.valueGetter != null ? this.valueGetter(tableCellEventGenerator()) : value;
    }

    setValue(event: TableCellEvent): void {
        if (this.valueSetter != null) {
            this.valueSetter(event);
        }
        else if (event.row.values[event.column.colId] instanceof TableCell) {
                event.row.values[event.column.colId].value = event.value;
            } 
            else {
                event.row.values[event.column.colId] = event.value;
            }
    }

    formatValue(event: TableCellEvent, locale = "de"): string {
        if (this.valueFormatter != null) {
            return this.valueFormatter(event);
        }
        switch (this.cellType) {
            case TableCellType.int:
                return Utils.isInt(event.value) ? Utils.formatNumber(Utils.toNumber(event.value), 0, false, true, locale != "de") : event.value;
            case TableCellType.decimal:
                return Utils.isNumber(event.value) ? Utils.formatNumber(Utils.toNumber(event.value), this.numDecimalDigits ?? 10, this.expandDecimalDigits, true, locale != "de") : event.value;
            case TableCellType.date:
                return Utils.isNoe(event.value) ? "" : Dates.formatDefaultDate(Dates.to(event.value));
            case TableCellType.dateTime:
                return Utils.isNoe(event.value) ? "" : Dates.formatDefault(Dates.to(event.value));
            case TableCellType.duration:
                return event.value == null ? "" : Utils.toDurationStringAdvanced(Utils.toNumber(event.value), 2, true);
            case TableCellType.durationNanos:
                return event.value == null ? "" : Utils.toDurationStringAdvancedNanos(Utils.toNumber(event.value), 2, true);
            case TableCellType.byteSize:
                return event.value == null ? "" : Utils.toByteSizeString(Utils.toNumber(event.value), false);
            case TableCellType.button:
                return "";
            default:
                return event.value;
        }
    }

    toCsvString(value: any): string {
        value = TableCell.getValue(value);
        switch (this.cellType) {
            case TableCellType.date:
                return Dates.format(value as Date, "dd.mm.yyyy");
            case TableCellType.dateTime:
                return Dates.formatDefault(value as Date);
            case TableCellType.bool:
                return Utils.toBool(value) ? "Ja" : "Nein";
            default:
                return Objects.toString(value);
        }
    }

    getFullCaption(): string {
        return (Utils.isNoe(this.groupCaption) ? "" : this.groupCaption + " ") + this.caption;
    }

    getWidth(): string {
        return this.calculatedWidth;
    }

    toFrontendField(): FrontendField {
        let result = new FrontendField(this.id, this.caption, FrontendFieldType.string, { enabled: true, maxLength: this.editableMaxLength });
        switch (this.cellType) {
            case TableCellType.string:
                break;
            case TableCellType.int:
                result.type = FrontendFieldType.int;
                break;
            case TableCellType.decimal:
                result.type = FrontendFieldType.decimal;
                break;
            case TableCellType.bool:
                result.type = FrontendFieldType.bool;
                break;
            case TableCellType.date:
                result.type = FrontendFieldType.date;
                break;
            case TableCellType.dateTime:
                result.type = FrontendFieldType.dateTime;
                break;
            default:
                throw new Error("Der Zelltyp " + this.cellType + " kann nicht in FrontendFieldType konvertiert werden");
        }
        return result;
    }

    isFilterable(): boolean {
        return this.filterable ?? (this.cellType != TableCellType.button && this.cellType != TableCellType.color);
    }

    isSortable(): boolean {
        return this.sortable ?? (this.cellType != TableCellType.button && this.cellType != TableCellType.color);
    }

}